import { useRef, useEffect, forwardRef, ReactNode, RefObject } from "react";
import { useClickOutside } from "@/lib/hooks/use-click-outside";
import { useScrollLock } from "@/lib/hooks/use-scroll-lock";
import { cn } from "@/lib/utils";

interface DropdownFrameProps {
  className?: string;
  isVisible: boolean;
  setIsVisible: (visible: boolean) => void;
  border?: "no border" | "line border" | "drop shadow";
  closeOnClickOutside?: boolean;
  slideFrom?: "top" | "nav";
  children: ReactNode;
  toggleButtonRef?: RefObject<HTMLElement>;
}

const DropdownFrame = forwardRef<HTMLDivElement, DropdownFrameProps>(
  (
    {
      className = "",
      isVisible = false,
      setIsVisible = () => {},
      border = "no border",
      closeOnClickOutside = false,
      slideFrom = "top",
      children = null,
      toggleButtonRef,
    },
    ref,
  ) => {
    const { setScrollLocked } = useScrollLock();

    const localRef = useRef<HTMLDivElement>(null);
    const combinedRef = ref || localRef;

    useClickOutside(
      combinedRef as RefObject<HTMLDivElement>,
      () => {
        if (closeOnClickOutside) {
          setIsVisible(false);
        }
      },
      toggleButtonRef,
    );

    useEffect(() => {
      if (isVisible && slideFrom === "nav") {
        setScrollLocked(true);
      }

      return () => {
        setScrollLocked(false);
      };
    }, [isVisible, setScrollLocked, slideFrom]);

    return (
      <>
        <div
          ref={combinedRef}
          className={cn(
            "tw-fixed tw-transition-transform tw-ease-in-out tw-w-full tw-overflow-hidden tw-z-30",
            {
              "tw-translate-y-0": isVisible,
              "-tw-translate-y-full": !isVisible,
              "tw-top-0 tw-duration-300": slideFrom === "top",
              "tw-top-72 tw-duration-500": slideFrom === "nav",
              "tw-border-b tw-border-b-gray-300":
                isVisible && border === "line border",
              "tw-shadow-lg": isVisible && border === "drop shadow",
            },
            className,
          )}
        >
          {children}
        </div>
        {slideFrom === "nav" && (
          <div
            className={cn(
              "tw-fixed tw-inset-0 tw-z-20 tw-bg-overlay-dark tw-transition-opacity tw-duration-500 data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0",
              {
                "tw-opacity-100": isVisible,
                "tw-opacity-0": !isVisible,
              },
            )}
          />
        )}
      </>
    );
  },
);

DropdownFrame.displayName = "DropdownFrame";

export { DropdownFrame };
